import { Avatar, AvatarBadge, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Identicon } from "@/components/ui/identicon"
import { cn } from "@/lib/utils"

/** Size tokens must sit on the outer box. `size-full` on the circle alone collapses when that box has no size. */
const AVATAR_SIZE_TOKEN =
  /^(?:!?|[a-z0-9-]+:)*(?:size-|h-|w-|min-h-|min-w-|max-h-|max-w-|shrink-|grow(?:-|$)|basis-)/

function splitAvatarClassName(className?: string) {
  const size: string[] = []
  const rest: string[] = []
  for (const token of className?.trim().split(/\s+/) ?? []) {
    if (!token) continue
    if (AVATAR_SIZE_TOKEN.test(token)) size.push(token)
    else rest.push(token)
  }
  return { sizeClass: size.join(" "), restClass: rest.join(" ") }
}

export interface InitialsAvatarProps {
  src?: string | null
  /** Stabilises identicon pattern (e.g. user id, profile id, email). */
  colorSeed: string
  className?: string
  alt?: string
  /** 6px card ring — identity heroes (private + public share) and matching editors only. */
  framed?: boolean
  /** Destructive dot (e.g. unread notifications on mobile account menu). */
  showNotificationDot?: boolean
}

/**
 * User avatar with optional uploaded photo; geometric identicon when no photo.
 * Uses container-query sizing on the outer Avatar (e.g. `size-8`, `size-10`).
 */
export function InitialsAvatar({
  src,
  colorSeed,
  className,
  alt = "",
  framed = false,
  showNotificationDot = false,
}: InitialsAvatarProps) {
  const key = src ? `with-photo:${src}` : `identicon:${colorSeed}`
  const { sizeClass, restClass } = splitAvatarClassName(className)

  return (
    <span className={cn("relative inline-flex shrink-0 size-8", sizeClass)}>
      <Avatar
        /* Radix keeps imageLoadingStatus as "loaded" after a photo mounts; removing AvatarImage
         * does not reset it, so AvatarFallback stays hidden. Remount when photo vs identicon. */
        key={key}
        className={cn("size-full", framed && "border-[6px] border-card", restClass)}
      >
        {src ? <AvatarImage src={src} alt={alt} className="object-cover" /> : null}
        <AvatarFallback className="rounded-full border-0 flex size-full items-center justify-center overflow-hidden p-0">
          <Identicon seed={colorSeed} />
        </AvatarFallback>
      </Avatar>
      {showNotificationDot ? (
        <AvatarBadge
          aria-hidden
          className="right-px top-px size-2 bg-destructive ring-sidebar"
        />
      ) : null}
    </span>
  )
}
